import _debounce from 'lodash/debounce';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import SearchIcon from '@/assets/icons/search.svg';
import { CloseIcon } from '@/components/icons/close';
import { Input } from '@/components/ui/input';
import { cn } from '@/utils/classnames';

interface SearchProps {
  setSearchQuery: Dispatch<SetStateAction<string | undefined>>;
  placeholder?: string;
  className?: string;
  // Optional initial value for the input, e.g. hydrated from a URL search
  // param so the field reflects the active search on load/back-navigation.
  // Only used to seed the input's initial state — the component otherwise
  // stays uncontrolled/debounced as before.
  initialValue?: string;
}

export function Search({
  setSearchQuery,
  placeholder,
  className,
  initialValue,
}: SearchProps) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState<string | undefined>(
    initialValue,
  );

  const debouncedSetSearchQuery = useMemo(
    () => _debounce((value: string) => setSearchQuery(value), 500),
    [setSearchQuery],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setInputValue(value);
      debouncedSetSearchQuery(value);
    },
    [debouncedSetSearchQuery],
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    debouncedSetSearchQuery.cancel();
    setSearchQuery('');
  }, [debouncedSetSearchQuery, setSearchQuery]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    },
    [],
  );

  return (
    <div className={cn('relative w-[340px]', className)}>
      <Input
        placeholderIcon={SearchIcon}
        placeholder={
          placeholder ??
          intl.formatMessage({
            defaultMessage: 'Type to filter...',
            description:
              'Search field hint on tables or lists; narrows visible rows as the user types',
          })
        }
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />
      {!!inputValue && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer"
        >
          <CloseIcon className="text-fx-neutral-400" />
        </button>
      )}
    </div>
  );
}
